import { Component, computed, DOCUMENT, inject, input } from '@angular/core';
import { DocumentPathService, Subnavigation, toTitleCase } from '@common';
import { CONFIG } from '@config';
import { AccordionComponent } from '../accordion/accordion.component';
import { GridColumnComponent } from '../grid-column/grid-column.component';
import { GridContainerComponent } from '../grid-container/grid-container.component';
import { GridRowComponent } from '../grid-row/grid-row.component';
import { IconComponent } from '../icon/icon.component';

@Component({
  selector: 'app-footer',
  imports: [
    AccordionComponent,
    GridColumnComponent,
    GridContainerComponent,
    GridRowComponent,
    IconComponent,
  ],
  templateUrl: './footer.component.html',
  styleUrl: './footer.component.scss',
  standalone: true,
})
export class FooterComponent {
  private readonly config = inject(CONFIG);
  private readonly domainId = this.config.domainId;
  private readonly documentPathService = inject(DocumentPathService);
  private readonly privacyPolicyLink = this.config.privacyPolicyLink;
  private readonly document: Document = inject(DOCUMENT);
  private openCookieManagementButton: HTMLButtonElement | null = null;
  private readonly hasGuaranteTerms =
    this.config.domainFeatures.hasGuaranteeTerms;

  readonly guaranteeTermsUrl =
    this.documentPathService.guaranteeTermsAndConditionsUrl;
  readonly copyrightOwner = input<string>(
    'Domestic & General Insurance PLC. All rights reserved.',
  );

  readonly legalContent = input<string>(
    `Appliance Care policies are underwritten by Domestic & General Insurance PLC. Registered Office: Swan Court, 11 Worple Road, Wimbledon, London SW19 4JS, United Kingdom. Registered in England and Wales, Company No. 485850.
    Domestic & General Insurance PLC is authorised by the Prudential Regulation Authority and regulated by the Financial Conduct Authority and the Prudential Regulation Authority. Financial register number 202111.
    Domestic & General Insurance PLC is an insurance undertaking, not an intermediary. We are the underwriter of the insurance and do not provide a personal recommendation or advice. Domestic & General Insurance PLC pay their sales agents periodic incentives (such as bonus payments) if they meet certain quality and sales targets. All prices include all applicable taxes (including IPT).`,
  );

  readonly copyrightYear: number = new Date().getFullYear();

  readonly navigation = input<Subnavigation[]>([
    {
      title: 'Find out more',
      links: [
        {
          label: 'Contact us',
          href: `./support/contact-us`,
        },
        ...(this.hasGuaranteTerms
          ? [
              {
                label: `${toTitleCase(this.domainId)} guarantee terms and conditions`,
                href: this.guaranteeTermsUrl()!,
              },
            ]
          : []),
      ],
    },
    {
      title: 'Legal',
      links: [
        {
          label: 'Privacy policy',
          href: this.privacyPolicyLink ?? `./support/privacy-policy`,
        },
        {
          label: 'Cookies policy',
          href: `./support/cookies-policy`,
        },
        {
          label: 'Accessibility policy',
          href: `./support/accessibility-policy`,
        },
      ],
    },
  ]);

  readonly panels = computed(() =>
    this.navigation().map((subnav: Subnavigation) => ({
      body: '',
      ...subnav,
    })),
  );

  readonly socialLinksWithIcon: {
    icon?: string;
    label?: string;
    href: string | undefined;
  }[] = [
    {
      icon: 'facebook',
      href: 'https://www.facebook.com/DandGUK',
    },
    {
      icon: 'linkedin',
      href: 'https://www.linkedin.com/company/domestic-&-general/mycompany',
    },
    {
      icon: 'instagram',
      href: 'https://www.instagram.com/domesticandgeneral',
    },
    {
      icon: 'twitter',
      href: 'https://twitter.com/DandGUK',
    },
  ];

  openCookieManagement(): void {
    this.openCookieManagementButton = this.document.querySelector(
      '.ot-floating-button__open',
    );
    this.openCookieManagementButton?.click();
  }
}
